/* eslint-disable react-hooks/exhaustive-deps */
import {FC, useContext, useEffect, useMemo, ReactNode} from 'react'
import {useQuery} from 'react-query'
import {
  createResponseContext,
  initialQueryResponse,
  initialQueryState,
  PaginationState,
  QUERIES,
  stringifyRequestQuery,
} from '../../../../../../_metronic/helpers'
import {getInvoices} from './_requests'
import {Invoice} from './_models'
import {useQueryRequest} from './QueryRequestProvider'
import {getSendStateRefetchInterval} from './invoiceStatusHelpers'
import {useAuth} from '../../../../auth'
import {
  isAllBusinessSalesList,
  LATEST_INVOICE_LIST_QUERY,
  waitsForSelectedBusiness,
} from './invoiceListScope'
import {INVOICE_LIST_SEASON_ALL_KEY} from './invoiceSeasonSelection'

const QueryResponseContext = createResponseContext<Invoice>(initialQueryResponse)
type WithChildren = {
  children?: ReactNode
  baseMode?: number
}
const QueryResponseProvider: FC<WithChildren> = ({children, baseMode}) => {
  const {state, updateState} = useQueryRequest()
  const {currentUser} = useAuth()
  const normalizedBaseMode = baseMode || 1
  // Purchases and archives wait for a business: an admin without one gets no request. Sales stay
  // enabled and list every business's newest invoices for an admin with company 0.
  const adminWithoutCompany = waitsForSelectedBusiness(currentUser, normalizedBaseMode)
  const allBusinessSales = isAllBusinessSalesList(currentUser, normalizedBaseMode)
  const query = useMemo(() => {
    const requestQuery = stringifyRequestQuery(state)
    return allBusinessSales ? `${requestQuery}&${LATEST_INVOICE_LIST_QUERY}` : requestQuery
  }, [allBusinessSales, state])

  useEffect(() => {
    if (state.baseMode !== normalizedBaseMode) {
      updateState({
        baseMode: normalizedBaseMode,
        // Every business's sales have no season to choose; the export and bulk actions that read
        // this state then cover the same invoices as the list.
        invoiceSeason: allBusinessSales ? INVOICE_LIST_SEASON_ALL_KEY : undefined,
        page: 1,
      })
    }
  }, [allBusinessSales, baseMode, state.baseMode])

  const {
    isFetching,
    refetch,
    data: response,
    dataUpdatedAt,
  } = useQuery(`${QUERIES.INVOICES_LIST}-${query}`, ({signal}) => getInvoices(query, signal), {
    cacheTime: 0,
    enabled: state.baseMode === normalizedBaseMode && !adminWithoutCompany,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    // While a freshly sent invoice on this page waits for the organisation's answer, the
    // page polls itself; the interval is re-evaluated on every response, so it stops by itself.
    refetchInterval: (data) => getSendStateRefetchInterval(data?.data),
    retry: false,
  })

  return (
    <QueryResponseContext.Provider
      value={{isLoading: isFetching, refetch, response, query, dataUpdatedAt}}
    >
      {children}
    </QueryResponseContext.Provider>
  )
}

const useQueryResponse = () => useContext(QueryResponseContext)

// One array for "no rows yet": a fresh [] on every render is a new value to every effect that
// depends on the rows, and the mobile list's effect stores it, so the list re-rendered in a loop
// for as long as the first response was on its way.
const NO_INVOICES: Invoice[] = []

const useQueryResponseData = () => {
  const {response} = useQueryResponse()
  if (!response) {
    return NO_INVOICES
  }

  return response?.data || NO_INVOICES
}

const useQueryResponsePagination = () => {
  const defaultPaginationState: PaginationState = {
    links: [],
    ...initialQueryState,
  }

  const {response} = useQueryResponse()
  if (!response || !response.payload || !response.payload.pagination) {
    return defaultPaginationState
  }

  return response.payload.pagination
}

const useQueryResponseLoading = (): boolean => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
}
